// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    AsyncPaginate,
    AsyncPaginateProps,
} from 'react-select-async-paginate';
import {
    GroupBase,
    ActionMeta,
    PropsValue,
} from 'react-select'; // Import this
import { selectInputStyles } from 'common/components/Select/styles';

export interface IAsyncSelectInputProps<
    OptionType,
    Group extends GroupBase<OptionType>,
    Additional,
    IsMulti extends boolean
> extends Omit<AsyncPaginateProps<OptionType, Group, Additional, IsMulti>, 'onChange' | 'value' | 'defaultValue' | 'loadOptions'> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange?: (value: any, actionMeta: ActionMeta<OptionType>) => void;
    minHeight?: string | number;
    width?: string | number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value?: PropsValue<any> | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultValue?: PropsValue<any> | null;
    loadOptions?: AsyncPaginateProps<OptionType, Group, Additional, IsMulti>['loadOptions'] | null;
    hasErrors?: boolean;
}

export default function AsyncSelectInput<
    OptionType,
    Additional,
    Group extends GroupBase<OptionType> = GroupBase<OptionType>,
    IsMulti extends boolean = false
>(props: IAsyncSelectInputProps<OptionType, Group, Additional, IsMulti>) {
    const { isSearchable, onChange, value, defaultValue, loadOptions, ...remain } = props;

    const safeLoadOptions = loadOptions || (async () => ({
        options: [],
        hasMore: false,
    }));

    const actualProps = {
        ...remain,
        isSearchable,
        onChange,
        value,
        defaultValue,
        loadOptions: safeLoadOptions,
        components: {
            ...remain.components,
            IndicatorSeparator: () => null,
        },
        styles: {
            ...selectInputStyles,
            control: (base) => ({
                ...base,
                minHeight: props.minHeight,
                width: props.width,
            }),
        },
    } as AsyncPaginateProps<OptionType, Group, Additional, IsMulti>;

    return <AsyncPaginate {...actualProps} />;
}
